import { SetupWorker, setupWorker } from 'msw/browser';

import { isLocalOrDevelopmentEnvironment } from '../config';
import { ApplicationState } from '../domain/types';
import { useLocalStorageValue } from '../hooks/useLocalStorage';
import { AppDispatch, invalidateStore, store } from '../store/store';
import { DEMO_MODE_ACTIVE_STORAGE_KEY, isDemoModeActive, setDemoModeActive } from './demoModeState';
import { handlers } from './handlers';
import { mockedUnhandledRequestPattern } from './handlers/mockedUnhandledRequestPattern';

let serviceWorker: SetupWorker | null = null;
const ROLE_STORAGE_KEY = 'role';
const MOCK_APPLICATION_STORAGE_KEY = 'mockApplication';

export type MockedSession = {
    state?: ApplicationState;
    numberOfDebts: number;
    declineReason: string;
};

const defaultMockedSession = { numberOfDebts: 2, declineReason: 'OTHER' };

/**
 * Listens to the local storage and converts the result to a mocked session
 *
 * @returns the mocked session
 */
export const useMockedSession = (): MockedSession => {
    const mockedSession =
        useLocalStorageValue(MOCK_APPLICATION_STORAGE_KEY, JSON.stringify(defaultMockedSession)) ??
        defaultMockedSession;
    return JSON.parse(mockedSession);
};

const isMockingAvailable = () => {
    return isLocalOrDevelopmentEnvironment;
};

const setMockedSession = (session: MockedSession | undefined) => {
    if (!isDemoModeActive()) {
        return;
    }
    localStorage.setItem(MOCK_APPLICATION_STORAGE_KEY, JSON.stringify(session));
    window.dispatchEvent(new Event('storage'));
};

const getMockedSession = () => {
    return (
        (JSON.parse(
            localStorage.getItem(MOCK_APPLICATION_STORAGE_KEY) ?? JSON.stringify(defaultMockedSession),
        ) as MockedSession) ?? defaultMockedSession
    );
};

const isMainApplicant = () => {
    return (localStorage.getItem(ROLE_STORAGE_KEY) ?? 'mainApplicant') === 'mainApplicant';
};

const isDraft = () => {
    return (localStorage.getItem(ROLE_STORAGE_KEY) ?? 'draft') === 'draft';
};

const isCoApplicantDraft = () => {
    return (localStorage.getItem(ROLE_STORAGE_KEY) ?? 'coApplicantDraft') === 'coApplicantDraft';
};

const getMockServiceWorker = () => {
    if (serviceWorker) return serviceWorker;
    serviceWorker = setupWorker(...handlers);
    return serviceWorker;
};

const start = async (role: 'mainApplicant' | 'coApplicant' | 'draft' | 'coApplicantDraft') => {
    if (!isMockingAvailable()) {
        return;
    }

    if (isDemoModeActive()) {
        return;
    }
    setDemoModeActive(true);
    localStorage.setItem(DEMO_MODE_ACTIVE_STORAGE_KEY, 'true');
    localStorage.setItem(ROLE_STORAGE_KEY, role);
    window.dispatchEvent(new Event('storage'));
    await getMockServiceWorker().start({
        onUnhandledRequest(request, print) {
            const url = new URL(request.url);
            if (mockedUnhandledRequestPattern.some((pattern) => url.href.includes(pattern))) {
                return;
            }

            print.warning();
        },
    });
};

// `dispatch` defaults to the app-wide singleton store's dispatch for every real caller (DemoHeader.tsx,
// CalculatorPage.tsx), but is accepted as a parameter so a test can inject an isolated test store's dispatch
// instead -- otherwise this would silently invalidate the wrong store's state when exercised from a test
// that renders against its own createStore() instance rather than the singleton.
const stop = (dispatch: AppDispatch = store.dispatch) => {
    // Fire-and-forget, and safely so: invalidateAuthentication clears authentication state synchronously up
    // front (see its own doc comment) and invalidateForm's dispatch is synchronous too, so nothing here
    // needs to be async either. A refresh/exchange still in flight for the now-cleared session is separately
    // guarded by the identity checks in authenticationStore.ts, so a slow or hung revoke_token call has no
    // effect on subsequent state no matter how long it takes.
    invalidateStore(dispatch);
    setDemoModeActive(false);
    if (serviceWorker) serviceWorker.stop();
    localStorage.removeItem(DEMO_MODE_ACTIVE_STORAGE_KEY);
    localStorage.removeItem(ROLE_STORAGE_KEY);
    localStorage.removeItem(MOCK_APPLICATION_STORAGE_KEY);
    window.dispatchEvent(new Event('storage'));
};

const initialize = async () => {
    if (!isMockingAvailable()) {
        return;
    }
    const storedState = localStorage.getItem(DEMO_MODE_ACTIVE_STORAGE_KEY);
    let storedRole: 'mainApplicant' | 'coApplicant' | 'draft' | 'coApplicantDraft';
    if (isMainApplicant()) {
        storedRole = 'mainApplicant';
    } else if (isDraft()) {
        storedRole = 'draft';
    } else if (isCoApplicantDraft()) {
        storedRole = 'coApplicantDraft';
    } else {
        storedRole = 'coApplicant';
    }

    if (storedState === 'true') {
        await start(storedRole);
    }
};

export const MockServiceWorker = {
    start,
    stop,
    initialize,
    isActive: isDemoModeActive,
    setMockedSession,
    getMockedSession,
    isMainApplicant,
    isDraft,
    isCoApplicantDraft,
    isMockingAvailable,
    DEMO_MODE_ACTIVE_STORAGE_KEY,
} as const;
